import { mapCurrencyAmountStrToInt } from "@circle-react/components/Paywalls/Admin/utils";
import type { PaywallPriceLikeCurrency } from "@circle-react/components/shared/Paywalls/components/PaywallPriceLabel/types";
import type {
  PaywallCurrency,
  PaywallLowestPrice,
} from "@circle-react/types/Paywall";
import {
  paywallPriceStatuses,
  paywallPriceTypes,
} from "@circle-react/types/PaywallPrice";
import type {
  PaywallPrice,
  PaywallPriceBuildOptionsEntry,
  PaywallPriceBuildOptionsFrequency,
  PaywallPriceFrequency,
  PaywallPriceInterval,
  PaywallPriceScenario,
  PaywallPriceScenarioCamelCase,
} from "@circle-react/types/PaywallPrice";
import { filter } from "lodash";

import { t } from "@/i18n-js/instance";

import { formatPaywallCurrencyAmount } from "./paywalls/currencies";
import { getPriceIntervalLabel } from "./paywalls/paywallPricePresenter";

export const PRICE_SCENARIOS: Record<
  PaywallPriceScenarioCamelCase,
  PaywallPriceScenario
> = {
  value: "value",
  trialThenValue: "trial_then_value",
  upfrontThenValue: "upfront_then_value",
  trialThenUpfrontThenValue: "trial_then_upfront_then_value",
  upfrontThenTrialThenValue: "upfront_then_trial_then_value",
};

export const PRICE_TRIAL_ELIGIBILITY_CONTEXTS = {
  addingPrice: "adding_price",
  addingSubscription: "adding_subscription",
};

export const isSubscriptionPrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.subscription;
export const isInstallmentsPrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.installments;
export const isOneTimePrice = (paywallPrice: any) =>
  paywallPrice?.price_type === paywallPriceTypes.onetime;

export const isRecurringPrice = (paywallPrice: any) =>
  isSubscriptionPrice(paywallPrice) || isInstallmentsPrice(paywallPrice);

export const recurringPrices = (paywallPrices: any) =>
  filter(paywallPrices, paywallPrice => isRecurringPrice(paywallPrice));

export const isPriceActive = (paywallPrice: PaywallPrice) =>
  paywallPrice.status === paywallPriceStatuses.active;

export const isPriceEligibleForTrial = (
  paywallPrice: any,
  context: any = PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingSubscription,
) =>
  isRecurringPrice(paywallPrice) &&
  (context == PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingPrice ||
    !paywallPrice.upfront_payment_amount);

export const subscriptionPrices = (paywallPrices: any) =>
  filter(paywallPrices, paywallPrice => isSubscriptionPrice(paywallPrice));

export const activeSubscriptionPrices = (paywallPrices: PaywallPrice[]) =>
  filter(
    paywallPrices,
    paywallPrice =>
      isSubscriptionPrice(paywallPrice) && isPriceActive(paywallPrice),
  );

export const pricesEligibleForTrial = (
  paywallPrices: any,
  context: any = PRICE_TRIAL_ELIGIBILITY_CONTEXTS.addingSubscription,
) =>
  filter(paywallPrices, paywallPrice =>
    isPriceEligibleForTrial(paywallPrice, context),
  );

export const findFrequencyByIntervalAndCount = (
  buildOptions: PaywallPriceBuildOptionsEntry,
  interval?: PaywallPriceInterval,
  interval_count?: number,
) =>
  Object.values(buildOptions.frequencies).find(
    (frequency: PaywallPriceBuildOptionsFrequency) =>
      frequency.interval === interval &&
      frequency.interval_count === interval_count,
  );

export const displayPaywallPrice = (paywall: any) => {
  const preferredPrice =
    paywall.stripe_prices_attributes.find(
      (price_attribute: any) => price_attribute.preferred,
    ) ?? paywall.stripe_prices_attributes[0];

  return preferredPrice
    ? preferredPrice.short_description
    : t("settings.paywalls.list.form.no_preferred_price_selected");
};

// PR review finding F: getPriceIntervalLabel / paywall_checkout.price_value
// .interval_suffix is shared with the checkout page and post-purchase email,
// where "biannually"/"biennial"/"bimonthly"/"biweekly" are deliberately full
// phrases ("every 6 months") written for a sentence template, not a "/X"
// suffix -- correct there, but they render nonsensically after a slash here
// ("From $60 /every 6 months"). biannually and biennial are admin-creatable
// today, so this is reachable, not theoretical. Rather than changing the
// shared map (and risking those other consumers), override just these four
// cadences with the short forms this app already uses elsewhere for a "/X"
// suffix (per_biannual, custom_pricing_interval_biannual, biennially_label);
// the other five cadences (mo, yr, quarter, week, day) are already short and
// pass through unchanged.
const PICKER_INTERVAL_SUFFIX_OVERRIDE_LOCALE_ROOT =
  "settings.paywalls.modal.tabs.upsell.forms.paywall_selection.interval_suffix_overrides";
const PICKER_INTERVAL_SUFFIX_OVERRIDES: readonly PaywallPriceFrequency[] = [
  "biannually",
  "biennial",
  "bimonthly",
  "biweekly",
];

const getPickerPriceIntervalSuffix = (
  intervalType: PaywallPriceFrequency,
): string =>
  PICKER_INTERVAL_SUFFIX_OVERRIDES.includes(intervalType)
    ? t([PICKER_INTERVAL_SUFFIX_OVERRIDE_LOCALE_ROOT, intervalType])
    : getPriceIntervalLabel(intervalType);

// Standardized "starting from" suffix for the Add-on and Upgrade "Choose
// paywall" pickers (PF-423 Slice 2c sub-task 6) -- one field, one formatter,
// both pickers. Unlike displayPaywallPrice (a raw preferred/first price, not
// a "starting from" computation), this reads the server-computed
// `lowest_price` (Paywall#lowest_subscription_price -- active subscription
// prices only, normalized across billing intervals server-side via
// monthly_normalized_amount, which has no client-side equivalent and must
// not be re-derived here). Displays the price's own actual amount and
// cadence (not the normalized-monthly figure) -- monthly_normalized_amount is
// selection criteria only, per the human's review decision.
//
// A paywall with no active subscription price (onetime/installments-only)
// has no `lowest_price` and renders no suffix at all, matching the existing
// "no eligible price" precedent already used by the Upgrade picker's suffix
// computation (PR #55538) rather than the `no_preferred_price_selected`
// sentence, which was written for a different surface. Product may revisit
// this default later.
export const getPaywallPickerPriceSuffix = (
  paywall: { lowest_price?: PaywallLowestPrice | null },
  currency: PaywallPriceLikeCurrency,
): string | undefined => {
  const { lowest_price: lowestPrice } = paywall;
  if (!lowestPrice) return undefined;

  return t(
    "settings.paywalls.modal.tabs.upsell.forms.paywall_selection.price_suffix",
    {
      amount: formatPaywallCurrencyAmount(currency, lowestPrice.amount, {
        amountInCents: true,
        includeCurrencyCode: false,
      }),
      interval: getPickerPriceIntervalSuffix(lowestPrice.interval_type),
    },
  );
};
export const upfrontPaymentVariants = {
  noUpfrontPayment: "no_upfront_payment",
  upfrontPayment: "upfront_payment",
} as const;

export type UpfrontPaymentDetails =
  | {
      variant: "upfront_payment";
      amountInCents: number;
      amountFormatted: string;
    }
  | {
      variant: "no_upfront_payment";
      amountInCents?: never;
      amountFormatted?: never;
    };
export const resolveUpfrontPaymentDetails = (
  currency: PaywallCurrency,
  upfrontAmount: string | number | undefined,
  options?: { shouldShowCurrency?: boolean },
): UpfrontPaymentDetails => {
  if (!upfrontAmount) {
    return { variant: upfrontPaymentVariants.noUpfrontPayment };
  }
  const amountInCents = mapCurrencyAmountStrToInt(upfrontAmount);
  if (!amountInCents) {
    return { variant: upfrontPaymentVariants.noUpfrontPayment };
  }
  const amountFormatted = formatPaywallCurrencyAmount(currency, amountInCents, {
    amountInCents: true,
    includeCurrencyCode: options?.shouldShowCurrency ?? true,
  });
  return {
    variant: upfrontPaymentVariants.upfrontPayment,
    amountInCents,
    amountFormatted,
  };
};

export const calculateInstallmentAmount = (
  upfrontPaymentDetails: UpfrontPaymentDetails,
  amount: number,
  count: number,
) => {
  if (
    upfrontPaymentDetails.variant === upfrontPaymentVariants.noUpfrontPayment
  ) {
    return Math.round(amount / count);
  }
  return Math.round((amount - upfrontPaymentDetails.amountInCents) / count);
};

export const isTrialThenValue = (priceScenario?: PaywallPriceScenario) =>
  priceScenario === PRICE_SCENARIOS.trialThenValue;

export const isUpfrontThenValue = (priceScenario?: PaywallPriceScenario) =>
  priceScenario === PRICE_SCENARIOS.upfrontThenValue;

export const isUpfrontThenTrialThenValue = (
  priceScenario?: PaywallPriceScenario,
) => priceScenario === PRICE_SCENARIOS.upfrontThenTrialThenValue;

export const isTrialThenUpfrontThenValue = (
  priceScenario?: PaywallPriceScenario,
) => priceScenario === PRICE_SCENARIOS.trialThenUpfrontThenValue;

const INTERVAL_TYPE_MAPPER: Record<
  string,
  Record<number, PaywallPriceFrequency>
> = Object.freeze({
  monthly: {
    [1]: "monthly",
    [2]: "bimonthly",
    [3]: "quarterly",
    [6]: "biannually",
  },
  yearly: {
    [1]: "yearly",
    [2]: "biennial",
  },
  weekly: {
    [1]: "weekly",
    [2]: "biweekly",
  },
  daily: {
    [1]: "daily",
  },
});

export const getIntervalType = (price: {
  interval?: PaywallPriceInterval;
  interval_count?: number;
}): PaywallPriceFrequency => {
  if (
    price.interval &&
    price.interval_count &&
    INTERVAL_TYPE_MAPPER[price.interval]
  ) {
    return INTERVAL_TYPE_MAPPER[price.interval][price.interval_count];
  }
  return "onetime";
};
